<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { roleBadge, roleLabel } from '@/composables/useRoles';
import { normalizeForSearch } from '@/functions/utils';

interface User {
  id: number;
  email: string;
  name: string;
  firstname: string;
  nickname: string;
  role: string;
  roles?: string[];
  is_active: boolean;
  profile_picture?: string;
  club_name?: string;
  club_number?: number;
  comite_code?: string;
  ligue_code?: string;
  license_number?: number;
  license_club_name?: string;
  license_season?: string;
  registered_via?: string;
  registered_via_label?: string;
}

interface Role { id: number; role: string; }
interface Club { id: number; name: string; number?: number; }
interface Comite { id: number; code: string; name: string; }
interface Ligue { id: number; code: string; name: string; }

const users = ref<User[]>([]);
const roles = ref<Role[]>([]);
const clubs = ref<Club[]>([]);
const comites = ref<Comite[]>([]);
const ligues = ref<Ligue[]>([]);
const loading = ref(true);
const search = ref('');
const sourceFilter = ref('');

const showModal = ref(false);
const editingUser = ref<User | null>(null);
const formRoles = ref<string[]>([]);
const formActive = ref(true);
const formClubId = ref<number | null>(null);
const formComiteId = ref<number | null>(null);
const formLigueId = ref<number | null>(null);
const formFirstname = ref('');
const formName = ref('');
const formNickname = ref('');
const formEmail = ref('');
const formPassword = ref('');
const saving = ref(false);

const needsClub = computed(() => formRoles.value.includes('club_representative'));
const needsComite = computed(() => formRoles.value.includes('comitee_representative'));
const needsLigue = computed(() => formRoles.value.includes('ligue_representative'));

const showCreateModal = ref(false);
const createForm = ref({ email: '', name: '', firstname: '', nickname: '', password: '', role: 'player' });
const creating = ref(false);

const filtered = computed(() => {
  const q = normalizeForSearch(search.value);
  return users.value.filter(u => {
    if (sourceFilter.value && u.registered_via !== sourceFilter.value) return false;
    if (!q) return true;
    return normalizeForSearch(u.email).includes(q) ||
      normalizeForSearch(u.name).includes(q) ||
      normalizeForSearch(u.firstname).includes(q) ||
      normalizeForSearch(u.nickname).includes(q);
  });
});

// Quantifie les inscriptions par site d'origine, pour répondre à "d'où viennent nos
// utilisateurs ?" sans avoir à dépouiller les notifications Telegram une par une.
const sourceCounts = computed(() => {
  const counts = new Map<string, { label: string; count: number }>();
  for (const u of users.value) {
    const key = u.registered_via || 'dartshub';
    const label = u.registered_via_label || key;
    const entry = counts.get(key);
    if (entry) entry.count++;
    else counts.set(key, { label, count: 1 });
  }
  return [...counts.entries()]
    .map(([value, { label, count }]) => ({ value, label, count }))
    .sort((a, b) => b.count - a.count);
});


const load = async () => {
  loading.value = true;
  try {
    const [uRes, rRes, cRes, comRes, ligRes] = await Promise.all([
      dartsHubApi.get('/users/'),
      dartsHubApi.get('/users/roles'),
      dartsHubApi.get('/clubs/'),
      dartsHubApi.get('/clubs/comites'),
      dartsHubApi.get('/clubs/ligues'),
    ]);
    users.value = uRes.data;
    roles.value = rRes.data;
    clubs.value = cRes.data;
    comites.value = comRes.data;
    ligues.value = ligRes.data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const openEdit = (user: User) => {
  editingUser.value = user;
  formRoles.value = [...(user.roles?.length ? user.roles : [user.role])];
  formActive.value = user.is_active;
  formFirstname.value = user.firstname;
  formName.value = user.name;
  formNickname.value = user.nickname;
  formEmail.value = user.email;
  formPassword.value = '';
  formClubId.value = user.club_name ? (clubs.value.find(c => c.name === user.club_name)?.id ?? null) : null;
  formComiteId.value = user.comite_code ? (comites.value.find(c => c.code === user.comite_code)?.id ?? null) : null;
  formLigueId.value = user.ligue_code ? (ligues.value.find(l => l.code === user.ligue_code)?.id ?? null) : null;
  showModal.value = true;
};

const saveUser = async () => {
  if (!editingUser.value) return;
  if (needsClub.value && !formClubId.value) { toast.error('Veuillez sélectionner un club.'); return; }
  if (needsComite.value && !formComiteId.value) { toast.error('Veuillez sélectionner un comité.'); return; }
  if (needsLigue.value && !formLigueId.value) { toast.error('Veuillez sélectionner une ligue.'); return; }
  saving.value = true;
  try {
    const payload: Record<string, unknown> = {
      roles: formRoles.value,
      is_active: formActive.value,
      firstname: formFirstname.value,
      name: formName.value,
      nickname: formNickname.value,
      email: formEmail.value,
    };
    if (formPassword.value) payload.password = formPassword.value;
    await dartsHubApi.patch(`/users/${editingUser.value.id}`, payload);
    if (needsClub.value && formClubId.value)
      await dartsHubApi.post('/clubs/representatives', { user_email: formEmail.value, club_id: formClubId.value });
    else if (!needsClub.value && editingUser.value.club_name)
      await dartsHubApi.delete(`/clubs/representatives/${formEmail.value}`).catch(() => { });
    if (needsComite.value && formComiteId.value)
      await dartsHubApi.post('/clubs/comite-representatives', { user_email: formEmail.value, comite_id: formComiteId.value });
    else if (!needsComite.value && editingUser.value.comite_code)
      await dartsHubApi.delete(`/clubs/comite-representatives/${formEmail.value}`).catch(() => { });
    if (needsLigue.value && formLigueId.value)
      await dartsHubApi.post('/clubs/ligue-representatives', { user_email: formEmail.value, ligue_id: formLigueId.value });
    else if (!needsLigue.value && editingUser.value.ligue_code)
      await dartsHubApi.delete(`/clubs/ligue-representatives/${formEmail.value}`).catch(() => { });

    const u = users.value.find(u => u.id === editingUser.value!.id);
    if (u) {
      u.roles = [...formRoles.value];
      u.role = formRoles.value[0] || 'user';
      u.is_active = formActive.value;
      u.firstname = formFirstname.value;
      u.name = formName.value;
      u.nickname = formNickname.value;
      u.email = formEmail.value;
      u.club_name = needsClub.value ? clubs.value.find(c => c.id === formClubId.value)?.name : undefined;
      u.club_number = needsClub.value ? clubs.value.find(c => c.id === formClubId.value)?.number : undefined;
      u.comite_code = needsComite.value ? comites.value.find(c => c.id === formComiteId.value)?.code : undefined;
      u.ligue_code = needsLigue.value ? ligues.value.find(l => l.id === formLigueId.value)?.code : undefined;
    }
    showModal.value = false;
    toast.success('Utilisateur mis à jour.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    saving.value = false;
  }
};

const deleteUser = async (user: User) => {
  if (!confirm(`Supprimer ${user.email} ?`)) return;
  try {
    await dartsHubApi.delete(`/users/${user.id}`);
    users.value = users.value.filter(u => u.id !== user.id);
    toast.success('Utilisateur supprimé.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

const createUser = async () => {
  creating.value = true;
  try {
    const { data } = await dartsHubApi.post('/users/', { ...createForm.value });
    users.value.push(data);
    showCreateModal.value = false;
    createForm.value = { email: '', name: '', firstname: '', nickname: '', password: '', role: 'player' };
    toast.success('Utilisateur créé.');
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    creating.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="page">
    <div class="page-header">
      <h1><i class="fa-solid fa-users" /> Utilisateurs</h1>
      <div style="display:flex;gap:0.75rem;align-items:center">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Rechercher…" />
        </div>
        <button class="btn btn-primary btn-sm" @click="showCreateModal = true">
          <i class="fa-solid fa-plus" /> Créer
        </button>
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="source-summary">
      <button class="source-chip" :class="{ active: !sourceFilter }" @click="sourceFilter = ''">
        Tous <span class="source-chip-count">{{ users.length }}</span>
      </button>
      <button v-for="s in sourceCounts" class="source-chip"
        :class="{ active: sourceFilter === s.value }" @click="sourceFilter = sourceFilter === s.value ? '' : s.value" :key="s.value">
        {{ s.label }} <span class="source-chip-count">{{ s.count }}</span>
      </button>
    </div>

    <div v-if="!loading" class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Utilisateur</th>
            <th>Email</th>
            <th>Rôle</th>
            <th>Origine</th>
            <th>Licence</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filtered" :key="user.id">
            <td>
              <div class="user-cell">
                <div class="user-initials">{{ (user.firstname?.[0] || '') + (user.name?.[0] || '') }}</div>
                <div>
                  <div class="user-name">{{ user.firstname }} {{ user.name }}</div>
                  <div class="user-nick">@{{ user.nickname }}</div>
                </div>
              </div>
            </td>
            <td class="email-cell">{{ user.email }}</td>
            <td>
              <div style="display:flex;flex-wrap:wrap;gap:0.25rem;margin-bottom:0.25rem">
                <span v-for="r in (user.roles?.length ? user.roles : [user.role])" class="badge" :class="roleBadge[r] || 'badge-gray'"
                  :key="r">{{ roleLabel[r] || r }}</span>
              </div>
              <span v-if="user.club_name" class="entity-tag">
                <i class="fa-solid fa-shield-halved" /> {{ user.club_name }} <span v-if="user.club_number">#{{
                  user.club_number }}</span>
              </span>
              <span v-if="user.comite_code" class="entity-tag">
                <i class="fa-solid fa-sitemap" /> {{ user.comite_code }}
              </span>
              <span v-if="user.ligue_code" class="entity-tag">
                <i class="fa-solid fa-trophy" /> {{ user.ligue_code }}
              </span>
            </td>
            <td>
              <span class="badge badge-gray">{{ user.registered_via_label || 'DartsHub' }}</span>
            </td>
            <td>
              <span v-if="user.license_number" class="badge badge-green">
                <i class="fa-solid fa-id-card" /> #{{ user.license_number }}
              </span>
              <span v-else class="badge badge-gray">
                <i class="fa-solid fa-link-slash" /> Non lié
              </span>
              <div v-if="user.license_club_name" class="license-detail">
                {{ user.license_club_name }}<span v-if="user.license_season"> · {{ user.license_season }}</span>
              </div>
            </td>
            <td>
              <span class="badge" :class="user.is_active ? 'badge-green' : 'badge-red'">
                {{ user.is_active ? 'Actif' : 'Inactif' }}
              </span>
            </td>
            <td>
              <div class="action-btns">
                <button class="btn btn-ghost btn-sm" @click="openEdit(user)">
                  <i class="fa-solid fa-pen" />
                </button>
                <button class="btn btn-danger btn-sm" @click="deleteUser(user)">
                  <i class="fa-solid fa-trash" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="7" style="text-align:center;color:var(--text-muted)">Aucun utilisateur trouvé</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="user in filtered" class="mobile-card" :key="user.id">
        <div class="card-top">
          <div class="user-cell">
            <div class="user-initials">{{ (user.firstname?.[0] || '') + (user.name?.[0] || '') }}</div>
            <div>
              <div class="user-name">{{ user.firstname }} {{ user.name }}</div>
              <div class="user-nick">@{{ user.nickname }}</div>
            </div>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:0.25rem">
            <span class="badge" :class="user.is_active ? 'badge-green' : 'badge-red'">
              {{ user.is_active ? 'Actif' : 'Inactif' }}
            </span>
            <div class="action-btns">
              <button class="btn btn-ghost btn-sm" @click="openEdit(user)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn btn-danger btn-sm" @click="deleteUser(user)">
                <i class="fa-solid fa-trash" />
              </button>
            </div>
          </div>
        </div>
        <div class="email-cell">{{ user.email }}</div>
        <div style="display:flex;flex-wrap:wrap;gap:0.25rem;margin-top:0.15rem">
          <span v-for="r in (user.roles?.length ? user.roles : [user.role])" class="badge" :class="roleBadge[r] || 'badge-gray'"
            :key="r">{{ roleLabel[r] || r }}</span>
          <span class="badge badge-gray">{{ user.registered_via_label || 'DartsHub' }}</span>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:0.35rem">
          <span v-if="user.club_name" class="entity-tag">
            <i class="fa-solid fa-shield-halved" /> {{ user.club_name }}<span v-if="user.club_number"> #{{
              user.club_number }}</span>
          </span>
          <span v-if="user.comite_code" class="entity-tag">
            <i class="fa-solid fa-sitemap" /> {{ user.comite_code }}
          </span>
          <span v-if="user.ligue_code" class="entity-tag">
            <i class="fa-solid fa-trophy" /> {{ user.ligue_code }}
          </span>
        </div>
        <div style="display:flex;align-items:center;gap:0.4rem;flex-wrap:wrap">
          <span v-if="user.license_number" class="badge badge-green">
            <i class="fa-solid fa-id-card" /> #{{ user.license_number }}
          </span>
          <span v-else class="badge badge-gray">
            <i class="fa-solid fa-link-slash" /> Non lié
          </span>
          <span v-if="user.license_club_name" class="license-detail">
            {{ user.license_club_name }}<span v-if="user.license_season"> · {{ user.license_season }}</span>
          </span>
        </div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucun
        utilisateur trouvé</div>
    </div>

    <div v-if="!loading" class="table-count">{{ filtered.length }} / {{ users.length }} utilisateurs</div>

    <!-- Modal édition -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Modifier {{ editingUser?.email }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Prénom</label>
            <input v-model="formFirstname" type="text" />
          </div>
          <div class="form-group">
            <label>Nom</label>
            <input v-model="formName" type="text" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Pseudo</label>
            <input v-model="formNickname" type="text" />
          </div>
          <div class="form-group">
            <label>Email</label>
            <input v-model="formEmail" type="email" />
          </div>
        </div>
        <div class="form-group">
          <label>Nouveau mot de passe <span style="color:var(--text-muted);font-weight:400">(laisser vide pour ne pas
              changer)</span></label>
          <input v-model="formPassword" type="password" placeholder="••••••••" />
        </div>
        <div class="form-group">
          <label>Rôles</label>
          <div class="roles-grid">
            <label v-for="r in roles" class="role-check" :key="r.id">
              <input v-model="formRoles" type="checkbox" :value="r.role" />
              <span class="badge" :class="roleBadge[r.role] || 'badge-gray'">{{ roleLabel[r.role] || r.role }}</span>
            </label>
          </div>
        </div>
        <div v-if="needsClub" class="form-group">
          <label><i class="fa-solid fa-shield-halved" /> Club</label>
          <select v-model="formClubId">
            <option :value="null" disabled>- Sélectionner un club -</option>
            <option v-for="c in clubs" :value="c.id" :key="c.id">{{ c.name }} <template v-if="c.number">#{{ c.number
            }}</template>
            </option>
          </select>
        </div>
        <div v-if="needsComite" class="form-group">
          <label><i class="fa-solid fa-sitemap" /> Comité</label>
          <select v-model="formComiteId">
            <option :value="null" disabled>- Sélectionner un comité -</option>
            <option v-for="c in comites" :value="c.id" :key="c.id">{{ c.code }} - {{ c.name }}</option>
          </select>
        </div>
        <div v-if="needsLigue" class="form-group">
          <label><i class="fa-solid fa-trophy" /> Ligue</label>
          <select v-model="formLigueId">
            <option :value="null" disabled>- Sélectionner une ligue -</option>
            <option v-for="l in ligues" :value="l.id" :key="l.id">{{ l.code }} - {{ l.name }}</option>
          </select>
        </div>
        <div class="form-group">
          <label>Statut</label>
          <select v-model="formActive">
            <option :value="true">Actif</option>
            <option :value="false">Inactif</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveUser">
            <i v-if="saving" class="fa-solid fa-spinner fa-spin" /> Sauvegarder
          </button>
        </div>
      </div>
    </div>

    <!-- Modal création -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Créer un utilisateur</h2>
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Prénom</label>
            <input v-model="createForm.firstname" type="text" />
          </div>
          <div class="form-group">
            <label>Nom</label>
            <input v-model="createForm.name" type="text" />
          </div>
        </div>
        <div class="form-group">
          <label>Pseudo</label>
          <input v-model="createForm.nickname" type="text" />
        </div>
        <div class="form-group">
          <label>Email</label>
          <input v-model="createForm.email" type="email" />
        </div>
        <div class="form-group">
          <label>Mot de passe</label>
          <input v-model="createForm.password" type="password" />
        </div>
        <div class="form-group">
          <label>Rôle</label>
          <select v-model="createForm.role">
            <option v-for="r in roles" :value="r.role" :key="r.id">{{ r.role }}</option>
          </select>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="creating" @click="createUser">
            <i v-if="creating" class="fa-solid fa-spinner fa-spin" /> Créer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-initials {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--bg-input);
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--primary);
  flex-shrink: 0;
}

.user-name {
  font-weight: 500;
  font-size: 0.875rem;
}

.user-nick {
  color: var(--text-muted);
  font-size: 0.75rem;
}

.email-cell {
  color: var(--text-muted);
  font-size: 0.8rem;
}

.license-detail {
  color: var(--text-muted);
  font-size: 0.78rem;
  margin-top: 2px;
}

.action-btns {
  display: flex;
  gap: 0.4rem;
}

.roles-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.role-check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}

.role-check input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: var(--primary);
  cursor: pointer;
}

.entity-tag {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
}

.source-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.source-chip {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  color: var(--text);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.source-chip:hover {
  border-color: var(--primary);
}

.source-chip.active {
  border-color: var(--primary);
  background: color-mix(in srgb, var(--primary) 10%, transparent);
  color: var(--primary);
  font-weight: 600;
}

.source-chip-count {
  color: var(--text-muted);
  font-size: 0.72rem;
}

.source-chip.active .source-chip-count {
  color: var(--primary);
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 380px;
  max-width: 520px;
  width: 90%;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.mobile-cards {
  display: none;
}

@media (max-width: 640px) {
  .table-wrapper {
    display: none;
  }

  .mobile-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
</style>
